/**
 * Appwrite Database Service — COMPATIBILITY SHIM
 *
 * This file now delegates to the new Cloudflare-backed API service
 * (src/services/api.ts). It preserves the same function signatures so
 * that existing component imports continue to work without changes.
 *
 * Requirements: 6.1
 */
import { list, get, create, update, remove } from './api';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api-client';
import { rewriteImagesInObject } from '../lib/image-url-rewriter';

// ─── Helpers ────────────────────────────────────────────────────────

/** Safe JSON parse with fallback */
function jp(val, fallback = null) {
  if (val === null || val === undefined) return fallback;
  if (typeof val !== 'string') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

/** Safe JSON stringify */
function js(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === 'string') return val;
  return JSON.stringify(val);
}

/**
 * The new API returns documents already cleaned (no Appwrite system fields).
 * This helper ensures the `id` and `_id` fields are present for compatibility.
 */
function ensureId(doc) {
  if (!doc) return null;
  if (!doc.id && doc._id) doc.id = doc._id;
  if (!doc._id && doc.id) doc._id = doc.id;
  return doc;
}

// ─── Villa Parsing (kept for component compatibility) ───────────────

const VILLA_JSON_FIELDS = [
  'pricing', 'facilities', 'images', 'locationHighlights', 'management',
  'ownerInfo', 'location', 'meals', 'rules', 'locationImages', 'youtubeShorts', 'spaces'
];

function parseVilla(doc) {
  if (!doc) return null;
  const v = ensureId(doc);
  for (const field of VILLA_JSON_FIELDS) {
    if (v[field] && typeof v[field] === 'string') {
      v[field] = jp(v[field], field === 'facilities' || field === 'images' ||
        field === 'locationHighlights' || field === 'rules' || field === 'locationImages' ||
        field === 'youtubeShorts' ? [] : null);
    }
  }
  if (v.pricing && typeof v.pricing === 'object') {
    if (v.discount === undefined && v.pricing.discount !== undefined) v.discount = v.pricing.discount;
    if (v.offerValidTill === undefined && v.pricing.offerValidTill !== undefined) v.offerValidTill = v.pricing.offerValidTill;
    if (v.offerMessage === undefined && v.pricing.offerMessage !== undefined) v.offerMessage = v.pricing.offerMessage;
    if (v.termsAndConditions === undefined && v.pricing.termsAndConditions !== undefined) v.termsAndConditions = v.pricing.termsAndConditions;
    if (v.isFeaturedOffer === undefined && v.pricing.isFeaturedOffer !== undefined) v.isFeaturedOffer = v.pricing.isFeaturedOffer;
  }
  if (v.ownerInfo) { v.owner = v.ownerInfo; delete v.ownerInfo; }

  // Build effective gallery from images + spaces + locationImages + meals.images
  const gallery = [];
  const seen = new Set();
  const push = (u) => { if (typeof u === 'string' && u && !seen.has(u)) { seen.add(u); gallery.push(u); } };

  let primary = v.images;
  if (typeof primary === 'string') {
    const t = primary.trim();
    primary = t.startsWith('[') ? jp(t, [t]) : [t];
  }
  if (Array.isArray(primary)) primary.forEach(push);
  if (typeof v.thumbnail === 'string') push(v.thumbnail);
  if (Array.isArray(v.spaces)) for (const s of v.spaces) if (s && typeof s.image === 'string') push(s.image);
  if (Array.isArray(v.locationImages)) v.locationImages.forEach(push);
  if (v.meals && Array.isArray(v.meals.images)) v.meals.images.forEach(push);
  v.images = gallery;

  return rewriteImagesInObject(v);
}

function serializeVilla(data) {
  const d = { ...data };
  if (d.owner && typeof d.owner === 'object') { d.ownerInfo = js(d.owner); delete d.owner; }
  for (const field of VILLA_JSON_FIELDS) {
    if (field === 'ownerInfo') continue;
    if (d[field] !== undefined && d[field] !== null && typeof d[field] !== 'string') {
      d[field] = js(d[field]);
    }
  }
  delete d.id; delete d._id;
  delete d.reviews;
  return d;
}

function parseBooking(doc) {
  if (!doc) return null;
  const b = ensureId(doc);
  if (b.villaData && typeof b.villaData === 'string') b.villaData = jp(b.villaData, {});
  return rewriteImagesInObject(b);
}

function parseReview(doc) {
  if (!doc) return null;
  const r = ensureId(doc);
  if (r.reply && typeof r.reply === 'string') r.reply = jp(r.reply, null);
  if (r.replies && typeof r.replies === 'string') r.replies = jp(r.replies, {});
  return rewriteImagesInObject(r);
}

function parseBlog(doc) {
  if (!doc) return null;
  const b = ensureId(doc);
  if (b.images && typeof b.images === 'string') b.images = jp(b.images, []);
  return rewriteImagesInObject(b);
}

function parseSpecialOccasion(doc) {
  if (!doc) return null;
  const s = ensureId(doc);
  if (s.facilities && typeof s.facilities === 'string') s.facilities = jp(s.facilities, []);
  return rewriteImagesInObject(s);
}

function parseCancellationRequest(doc) {
  if (!doc) return null;
  const c = ensureId(doc);
  if (c.bookingData && typeof c.bookingData === 'string') c.bookingData = jp(c.bookingData, {});
  return rewriteImagesInObject(c);
}

// ─── Subscribe Helpers (polling-based, replaces Appwrite realtime) ──

/**
 * Subscribe to a collection — polls the API periodically.
 * Returns an unsubscribe function.
 */
export function subscribeToCollection(collection, callback, filters = {}, parser = ensureId) {
  let cancelled = false;
  let intervalId = null;

  const fetchData = async () => {
    try {
      const res = await list(collection, filters);
      if (!cancelled) callback((res.documents || []).map(parser));
    } catch (err) {
      console.error(`Subscribe fetch error (${collection}):`, err);
    }
  };

  // Initial fetch
  fetchData();
  // Poll every 30 seconds
  intervalId = setInterval(fetchData, 30000);

  return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
}

/**
 * Subscribe to a single document (polling-based).
 */
export function subscribeToDocument(collection, docId, callback, parser = ensureId) {
  let cancelled = false;
  let intervalId = null;

  const fetchDoc = async () => {
    try {
      const doc = await get(collection, docId);
      if (!cancelled) callback(parser(doc));
    } catch (err) {
      console.error(`Subscribe doc error (${collection}/${docId}):`, err);
      if (!cancelled) callback(null);
    }
  };

  fetchDoc();
  intervalId = setInterval(fetchDoc, 30000);

  return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
}

// ─── VILLAS ─────────────────────────────────────────────────────────

export async function getVillas(extraQueries = []) {
  // Convert Appwrite Query objects to API filter params
  const filters = queriesToFilters(extraQueries);
  const res = await list('villas', filters);
  return (res.documents || []).map(parseVilla);
}

export async function getVilla(id) {
  const doc = await get('villas', id);
  return parseVilla(doc);
}

export async function createVilla(data) {
  const serialized = serializeVilla(data);
  const doc = await create('villas', serialized);
  return parseVilla(doc);
}

export async function updateVilla(id, data) {
  const serialized = serializeVilla(data);
  const doc = await update('villas', id, serialized);
  return parseVilla(doc);
}

export async function deleteVilla(id) {
  return remove('villas', id);
}

export function subscribeVillas(callback, queries = []) {
  const filters = queriesToFilters(queries);
  return subscribeToCollection('villas', callback, filters, parseVilla);
}

export function subscribeVilla(id, callback) {
  return subscribeToDocument('villas', id, callback, parseVilla);
}

// ─── USERS ──────────────────────────────────────────────────────────

export async function getUser(userId) {
  try {
    const doc = await get('users', userId);
    return ensureId(doc);
  } catch {
    return null;
  }
}

export async function createUser(userId, data) {
  const doc = await create('users', { id: userId, ...data });
  return ensureId(doc);
}

export async function updateUser(userId, data) {
  const { id, _id, ...rest } = data;
  const doc = await update('users', userId, rest);
  return ensureId(doc);
}

export async function getUsers(queries = []) {
  const filters = queriesToFilters(queries);
  const res = await list('users', filters);
  return (res.documents || []).map(ensureId);
}

export function subscribeUser(userId, callback) {
  return subscribeToDocument('users', userId, callback);
}

export function subscribeUsers(callback, queries = []) {
  const filters = queriesToFilters(queries);
  return subscribeToCollection('users', callback, filters);
}

// ─── BOOKINGS ───────────────────────────────────────────────────────

export async function getBookings(queries = []) {
  const filters = queriesToFilters(queries);
  const res = await list('bookings', filters);
  return (res.documents || []).map(parseBooking);
}

export async function getBooking(id) {
  const doc = await get('bookings', id);
  return parseBooking(doc);
}

export async function createBooking(data) {
  const serialized = { ...data };
  if (serialized.villaData && typeof serialized.villaData !== 'string') {
    serialized.villaData = js(serialized.villaData);
  }
  const doc = await create('bookings', serialized);
  return parseBooking(doc);
}

export async function updateBooking(id, data) {
  const serialized = { ...data };
  if (serialized.villaData && typeof serialized.villaData !== 'string') {
    serialized.villaData = js(serialized.villaData);
  }
  delete serialized.id; delete serialized._id;
  const doc = await update('bookings', id, serialized);
  return parseBooking(doc);
}

export async function deleteBooking(id) {
  return remove('bookings', id);
}

export function subscribeBookings(callback, queries = []) {
  const filters = queriesToFilters(queries);
  return subscribeToCollection('bookings', callback, filters, parseBooking);
}

// ─── VILLA AVAILABILITY ──────────────────────────────────────────────

export async function getVillaAvailability(villaId) {
  const res = await list('villa-availability', { villaId });
  return (res.documents || []).map(ensureId);
}

export async function createVillaAvailability(data) {
  const doc = await create('villa-availability', data);
  return ensureId(doc);
}

export async function deleteVillaAvailability(id) {
  return remove('villa-availability', id);
}

export function subscribeVillaAvailability(villaId, callback) {
  return subscribeToCollection('villa-availability', callback, { villaId });
}

// ─── REVIEWS ────────────────────────────────────────────────────────

export async function getReviews(villaId) {
  const res = await list('reviews', { villaId });
  return (res.documents || []).map(parseReview);
}

export async function createReview(data) {
  const serialized = { ...data };
  if (serialized.reply !== undefined) serialized.reply = js(serialized.reply);
  if (serialized.replies !== undefined) serialized.replies = js(serialized.replies);
  const doc = await create('reviews', serialized);
  return parseReview(doc);
}

export async function updateReview(id, data) {
  const serialized = { ...data };
  delete serialized.id; delete serialized._id;
  if (serialized.reply !== undefined) serialized.reply = js(serialized.reply);
  if (serialized.replies !== undefined) serialized.replies = js(serialized.replies);
  const doc = await update('reviews', id, serialized);
  return parseReview(doc);
}

export async function deleteReview(id) {
  return remove('reviews', id);
}

export function subscribeReviews(villaId, callback) {
  return subscribeToCollection('reviews', callback, { villaId }, parseReview);
}

// ─── WISHLISTS ──────────────────────────────────────────────────────

export async function getWishlists(userId) {
  const res = await list('wishlists', { userId });
  const obj = {};
  (res.documents || []).forEach(doc => { obj[doc.villaId] = true; });
  return obj;
}

export async function addToWishlist(userId, villaId) {
  return create('wishlists', { userId, villaId });
}

export async function removeFromWishlist(userId, villaId) {
  const res = await list('wishlists', { userId, villaId, limit: 1 });
  if (res.documents && res.documents.length > 0) {
    return remove('wishlists', res.documents[0].id);
  }
}

export function subscribeWishlists(userId, callback) {
  return subscribeToCollection('wishlists', (docs) => {
    const obj = {};
    docs.forEach(d => { obj[d.villaId] = true; });
    callback(obj);
  }, { userId });
}

// ─── CHATS (discontinued — stubs for compatibility) ─────────────────

export async function getChatMessages() { return []; }
export async function sendChatMessage(data) { console.warn('Chat is discontinued'); return data; }
export async function updateChatMessage() { return null; }
export async function deleteChatMessage() { return null; }
export function subscribeChatMessages() { return () => {}; }
export async function getChatThreads() { return []; }
export async function getUserChats() { return []; }
export function subscribeChats() { return () => {}; }

// ─── NOTIFICATIONS (discontinued — stubs for compatibility) ─────────

export async function getNotifications() { return []; }
export async function createNotification() { return null; }
export async function markNotificationRead() { return null; }
export function subscribeNotifications() { return () => {}; }

// ─── QUERIES (Owner Queries) ────────────────────────────────────────

export async function getQueries(queries = []) {
  const filters = queriesToFilters(queries);
  const res = await list('queries', filters);
  return (res.documents || []).map(ensureId);
}

export async function createQuery(data) {
  const doc = await create('queries', { ...data, createdAt: Date.now() });
  return ensureId(doc);
}

export async function updateQuery(id, data) {
  return update('queries', id, data);
}

export function subscribeQueries(callback, queries = []) {
  const filters = queriesToFilters(queries);
  return subscribeToCollection('queries', callback, filters);
}

// ─── USER QUERIES (Contact Us) ──────────────────────────────────────

export async function getUserQueries(queries = []) {
  const filters = queriesToFilters(queries);
  const res = await list('user-queries', filters);
  return (res.documents || []).map(ensureId);
}

export async function createUserQuery(data) {
  const doc = await create('user-queries', { ...data, timestamp: Date.now() });
  return ensureId(doc);
}

export async function updateUserQuery(id, data) {
  return update('user-queries', id, data);
}

export function subscribeUserQueries(callback, queries = []) {
  const filters = queriesToFilters(queries);
  return subscribeToCollection('user-queries', callback, filters);
}

// ─── BLOGS ──────────────────────────────────────────────────────────

export async function getBlogs(queries = []) {
  const filters = queriesToFilters(queries);
  const res = await list('blogs', filters);
  return (res.documents || []).map(parseBlog);
}

export async function getBlog(id) {
  const doc = await get('blogs', id);
  return parseBlog(doc);
}

export async function createBlog(data) {
  const serialized = { ...data };
  if (serialized.images && typeof serialized.images !== 'string') serialized.images = js(serialized.images);
  if (!serialized.cityKey && serialized.mainCity) {
    serialized.cityKey = serialized.mainCity.toLowerCase().replace(/\s+/g, '-');
  }
  const doc = await create('blogs', serialized);
  return parseBlog(doc);
}

export async function updateBlog(id, data) {
  const serialized = { ...data };
  delete serialized.id; delete serialized._id;
  if (serialized.images && typeof serialized.images !== 'string') serialized.images = js(serialized.images);
  if (serialized.mainCity && !serialized.cityKey) {
    serialized.cityKey = serialized.mainCity.toLowerCase().replace(/\s+/g, '-');
  }
  return update('blogs', id, serialized);
}

export async function deleteBlog(id) {
  return remove('blogs', id);
}

export function subscribeBlogs(callback, queries = []) {
  const filters = queriesToFilters(queries);
  return subscribeToCollection('blogs', callback, filters, parseBlog);
}

// ─── DESTINATIONS ───────────────────────────────────────────────────

export async function getDestinations(queries = []) {
  const filters = queriesToFilters(queries);
  const res = await list('destinations', filters);
  return (res.documents || []).map(ensureId);
}

export async function createDestination(data) {
  const doc = await create('destinations', data);
  return ensureId(doc);
}

export async function updateDestination(id, data) {
  delete data.id; delete data._id;
  return update('destinations', id, data);
}

export async function deleteDestination(id) {
  return remove('destinations', id);
}

export function subscribeDestinations(callback, queries = []) {
  const filters = queriesToFilters(queries);
  return subscribeToCollection('destinations', callback, filters);
}

// ─── PROMOTED VILLAS ────────────────────────────────────────────────

export async function getPromotedVillas() {
  const res = await list('promoted-villas', { orderBy: 'position', order: 'asc' });
  return (res.documents || []).map(ensureId);
}

export async function addPromotedVilla(villaId, position) {
  return create('promoted-villas', { villaId, position, addedAt: new Date().toISOString() });
}

export async function updatePromotedVilla(id, data) {
  return update('promoted-villas', id, data);
}

export async function removePromotedVilla(id) {
  return remove('promoted-villas', id);
}

export function subscribePromotedVillas(callback) {
  return subscribeToCollection('promoted-villas', callback, { orderBy: 'position', order: 'asc' });
}

// ─── SPECIAL OCCASIONS ──────────────────────────────────────────────

export async function getSpecialOccasions(queries = []) {
  const filters = queriesToFilters(queries);
  const res = await list('special-occasions', filters);
  return (res.documents || []).map(parseSpecialOccasion);
}

export async function createSpecialOccasion(data) {
  const serialized = { ...data };
  if (serialized.facilities && typeof serialized.facilities !== 'string') serialized.facilities = js(serialized.facilities);
  const doc = await create('special-occasions', serialized);
  return parseSpecialOccasion(doc);
}

export async function updateSpecialOccasion(id, data) {
  const serialized = { ...data };
  delete serialized.id; delete serialized._id;
  if (serialized.facilities && typeof serialized.facilities !== 'string') serialized.facilities = js(serialized.facilities);
  return update('special-occasions', id, serialized);
}

export async function deleteSpecialOccasion(id) {
  return remove('special-occasions', id);
}

export function subscribeSpecialOccasions(callback, queries = []) {
  const filters = queriesToFilters(queries);
  return subscribeToCollection('special-occasions', callback, filters, parseSpecialOccasion);
}

// ─── ANALYTICS ──────────────────────────────────────────────────────

export async function getAnalytics(villaId, type) {
  const res = await list('analytics', { villaId, type, limit: 1 });
  return res.documents && res.documents.length > 0 ? ensureId(res.documents[0]) : null;
}

export async function incrementAnalytics(villaId, type, field = 'count') {
  const existing = await getAnalytics(villaId, type);
  if (existing) {
    const updateData = { [field]: (existing[field] || 0) + 1 };
    if (type === 'visitors') {
      updateData.lastVisit = Date.now();
      const now = new Date();
      const lastVisit = new Date(existing.lastVisit || 0);
      if (lastVisit.toDateString() !== now.toDateString()) {
        updateData.today = 1;
      } else {
        updateData.today = (existing.today || 0) + 1;
      }
      updateData.thisMonth = (existing.thisMonth || 0) + 1;
      updateData.lastSevenDays = (existing.lastSevenDays || 0) + 1;
    }
    return update('analytics', existing.id, updateData);
  } else {
    const data = { villaId, type, count: 1 };
    if (type === 'visitors') {
      data.today = 1; data.lastSevenDays = 1; data.thisMonth = 1;
      data.lastMonth = 0; data.lastVisit = Date.now();
    }
    return create('analytics', data);
  }
}

export async function getAllAnalytics(villaId) {
  const res = await list('analytics', { villaId, limit: 10 });
  const result = {};
  (res.documents || []).forEach(doc => { result[doc.type] = ensureId(doc); });
  return result;
}

export function subscribeAnalytics(villaId, callback) {
  return subscribeToCollection('analytics', (docs) => {
    const result = {};
    docs.forEach(doc => { result[doc.type] = doc; });
    callback(result);
  }, { villaId });
}

// ─── CANCELLATION REQUESTS ─────────────────────────────────────────

export async function getCancellationRequests(queries = []) {
  const filters = queriesToFilters(queries);
  const res = await list('cancellation-requests', filters);
  return (res.documents || []).map(parseCancellationRequest);
}

export async function createCancellationRequest(data) {
  const serialized = { ...data };
  if (serialized.bookingData && typeof serialized.bookingData !== 'string') {
    serialized.bookingData = js(serialized.bookingData);
  }
  const doc = await create('cancellation-requests', serialized);
  return parseCancellationRequest(doc);
}

export async function updateCancellationRequest(id, data) {
  return update('cancellation-requests', id, data);
}

export function subscribeCancellationRequests(callback, queries = []) {
  const filters = queriesToFilters(queries);
  return subscribeToCollection('cancellation-requests', callback, filters, parseCancellationRequest);
}

// ─── LUXURY VILLAS ──────────────────────────────────────────────────

export async function getLuxuryVillas() {
  const res = await list('luxury-villas');
  return (res.documents || []).map(ensureId);
}

export async function addLuxuryVilla(villaId, order = 0) {
  return create('luxury-villas', { villaId, order, addedAt: new Date().toISOString() });
}

export async function removeLuxuryVilla(id) {
  return remove('luxury-villas', id);
}

// ─── LUXURY SHUFFLE DATA ───────────────────────────────────────────

const SHUFFLE_DOC_ID = 'shuffle-state';

export async function getLuxuryShuffleData() {
  try {
    const doc = await get('luxury-shuffle-data', SHUFFLE_DOC_ID);
    const d = ensureId(doc);
    if (d && d.shuffledOrder && typeof d.shuffledOrder === 'string') d.shuffledOrder = jp(d.shuffledOrder, []);
    return d;
  } catch {
    return null;
  }
}

export async function setLuxuryShuffleData(data) {
  const serialized = { ...data };
  if (serialized.shuffledOrder && typeof serialized.shuffledOrder !== 'string') {
    serialized.shuffledOrder = js(serialized.shuffledOrder);
  }
  try {
    return await update('luxury-shuffle-data', SHUFFLE_DOC_ID, serialized);
  } catch {
    return await create('luxury-shuffle-data', { id: SHUFFLE_DOC_ID, ...serialized });
  }
}

// ─── Query Conversion Helper ────────────────────────────────────────

/**
 * Convert Appwrite Query objects (or arrays of them) into simple filter params
 * that the new REST API understands.
 *
 * Appwrite Query objects are typically created via Query.equal(), Query.orderAsc(), etc.
 * They serialize to JSON strings like: '{"method":"equal","attribute":"villaId","values":["abc"]}'
 *
 * The new API accepts flat query params: ?villaId=abc&orderBy=field&order=asc&limit=N&offset=N
 */
function queriesToFilters(queries) {
  if (!queries || !Array.isArray(queries) || queries.length === 0) return {};

  const filters = {};

  for (const q of queries) {
    if (!q) continue;

    // If it's already a string (Appwrite Query serialized), try to parse it
    let parsed = q;
    if (typeof q === 'string') {
      try { parsed = JSON.parse(q); } catch { continue; }
    }

    // Handle Appwrite Query object format
    if (parsed && typeof parsed === 'object') {
      const method = parsed.method || parsed.type;
      const attribute = parsed.attribute;
      const values = parsed.values;

      switch (method) {
        case 'equal':
          if (attribute && values && values.length > 0) {
            filters[attribute] = values[0];
          }
          break;
        case 'orderAsc':
          filters.orderBy = attribute;
          filters.order = 'asc';
          break;
        case 'orderDesc':
          filters.orderBy = attribute;
          filters.order = 'desc';
          break;
        case 'limit':
          filters.limit = values ? values[0] : undefined;
          break;
        case 'offset':
          filters.offset = values ? values[0] : undefined;
          break;
        default:
          break;
      }
    }
  }

  return filters;
}
